import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/stores';
import { config } from '@/lib/config';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { History, CalendarClock, Download, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import type { NotificationHistory } from '@core/models';
import {
  NotificationStatusScheduled,
  NotificationStatusSending,
  NotificationStatusSent,
  NotificationStatusFailed,
  NotificationStatusCancelled,
} from '@core/models';

const NotificationHistory: React.FC = observer(() => {
  const { tenantStore } = useStores();
  const [histories, setHistories] = useState<NotificationHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<NotificationHistory | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [cancellingId, setCancellingId] = useState('');

  const tenantId = tenantStore.selectedTenant?.id;

  const fetchHistories = useCallback(async () => {
    if (!tenantId) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantId}/notifications/history`,
        {
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data: NotificationHistory[] = await response.json();
        setHistories(data || []);
      } else {
        toast.error('履歴の取得に失敗しました');
      }
    } catch (error) {
      console.error('Failed to fetch histories:', error);
      toast.error('履歴の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchHistories();
  }, [fetchHistories]);

  const scheduledItems = histories
    .filter((h) => h.status === NotificationStatusScheduled)
    .sort((a, b) => (a.scheduled_at || '').localeCompare(b.scheduled_at || ''));

  const pastItems = histories
    .filter((h) => h.status !== NotificationStatusScheduled)
    .sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''));

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const statusKeyOf = (history: NotificationHistory) => history.status || NotificationStatusSent;

  const statusBadgeMap: Record<string, { label: string; className: string }> = {
    [NotificationStatusScheduled]: { label: '配信予定', className: 'bg-amber-100 text-amber-800' },
    [NotificationStatusSending]: { label: '配信中', className: 'bg-blue-100 text-blue-800' },
    [NotificationStatusSent]: { label: '配信済み', className: 'bg-green-100 text-green-800' },
    [NotificationStatusFailed]: { label: '失敗あり', className: 'bg-red-100 text-red-800' },
    [NotificationStatusCancelled]: { label: 'キャンセル', className: 'bg-gray-100 text-gray-600' },
  };

  const getStatusBadge = (history: NotificationHistory) => {
    const key = statusKeyOf(history);
    const info = statusBadgeMap[key] || { label: key, className: 'bg-gray-100 text-gray-600' };
    const isTest = history.type === 'test';
    return (
      <div className="flex items-center gap-1">
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${info.className}`}>
          {info.label}
        </span>
        {isTest && (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800">
            テスト
          </span>
        )}
      </div>
    );
  };

  const typeLabel = (type?: string) => {
    switch (type) {
      case 'broadcast': return '全体配信';
      case 'multicast': return '配信種別配信';
      case 'targeted': return 'ターゲット配信';
      case 'test': return 'テスト配信';
      default: return '全体配信';
    }
  };

  const openDetail = (history: NotificationHistory) => {
    setSelectedHistory(history);
    setDetailOpen(true);
  };

  const handleCancel = async (history: NotificationHistory) => {
    if (!tenantId) return;
    if (!window.confirm(`「${history.title}」の予約配信をキャンセルしますか?`)) return;

    setCancellingId(history.id);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantId}/notifications/history/${history.id}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (response.ok) {
        toast.success('予約配信をキャンセルしました');
        await fetchHistories();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.error || 'キャンセルに失敗しました');
      }
    } catch (error) {
      console.error('Failed to cancel notification:', error);
      toast.error('キャンセルに失敗しました');
    } finally {
      setCancellingId('');
    }
  };

  const exportCsv = () => {
    if (!tenantId || histories.length === 0) return;

    const header = ['id', 'title', 'type', 'status', 'target_count', 'delivered_count', 'failed_count', 'sent_at', 'completed_at'];
    const escapeCell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = histories.map((h) => [
      h.id,
      h.title,
      h.type,
      statusKeyOf(h),
      String(h.target_count),
      String(h.delivered_count),
      String(h.failed_count),
      formatDate(h.sent_at),
      formatDate(h.completed_at),
    ].map(escapeCell).join(','));

    const csv = `\uFEFF${[header.join(','), ...rows].join('\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `notifications_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast.success('CSVをダウンロードしました');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            配信一覧
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv}>
              <Download className="h-4 w-4 mr-1" />
              CSV出力
            </Button>
            <Button variant="outline" size="sm" onClick={fetchHistories} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              更新
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="past">
            <TabsList className="mb-4">
              <TabsTrigger value="scheduled" className="gap-1">
                <CalendarClock className="h-4 w-4" />
                配信予定 ({scheduledItems.length})
              </TabsTrigger>
              <TabsTrigger value="past">
                配信履歴 ({pastItems.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="scheduled">
              {scheduledItems.length === 0 ? (
                <div className="py-8 text-sm text-muted-foreground text-center border rounded-lg">
                  配信予定はありません。通知送信画面から日時を指定して予約できます。
                </div>
              ) : (
                <div className="space-y-3">
                  {scheduledItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between border rounded-lg p-3"
                    >
                      <div>
                        <button
                          type="button"
                          className="font-medium hover:underline text-left"
                          onClick={() => openDetail(item)}
                        >
                          {item.title}
                        </button>
                        <div className="text-xs text-muted-foreground mt-1 flex gap-3">
                          <span>予約日時: {formatDate(item.scheduled_at)}</span>
                          <span>対象: {item.target_count}件</span>
                          <span>{typeLabel(item.type)}</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={cancellingId === item.id}
                        onClick={() => handleCancel(item)}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        キャンセル
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="past">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>送信日時</TableHead>
                    <TableHead>タイトル</TableHead>
                    <TableHead>種別</TableHead>
                    <TableHead>ステータス</TableHead>
                    <TableHead className="text-right">対象</TableHead>
                    <TableHead className="text-right">成功</TableHead>
                    <TableHead className="text-right">失敗</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pastItems.map((item) => (
                    <TableRow key={item.id} className="cursor-pointer" onClick={() => openDetail(item)}>
                      <TableCell className="whitespace-nowrap">{formatDate(item.sent_at)}</TableCell>
                      <TableCell className="max-w-[240px] truncate">{item.title}</TableCell>
                      <TableCell>{typeLabel(item.type)}</TableCell>
                      <TableCell>{getStatusBadge(item)}</TableCell>
                      <TableCell className="text-right">{item.target_count}</TableCell>
                      <TableCell className="text-right text-green-700">{item.delivered_count}</TableCell>
                      <TableCell className="text-right text-red-700">{item.failed_count}</TableCell>
                    </TableRow>
                  ))}
                  {pastItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        配信履歴がありません
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedHistory?.title}</DialogTitle>
            <DialogDescription>
              {typeLabel(selectedHistory?.type)} ・ {getStatusBadge(selectedHistory!)}
            </DialogDescription>
          </DialogHeader>

          {selectedHistory && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-md border p-2">
                  <div className="text-xs text-muted-foreground">対象数</div>
                  <div className="text-lg font-semibold">{selectedHistory.target_count}</div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-xs text-muted-foreground">成功 / 失敗</div>
                  <div className="text-lg font-semibold">
                    <span className="text-green-700">{selectedHistory.delivered_count}</span>
                    {' / '}
                    <span className="text-red-700">{selectedHistory.failed_count}</span>
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs text-muted-foreground mb-1">メッセージ</div>
                <div className="rounded-md border p-2 whitespace-pre-wrap">
                  {selectedHistory.message || '(なし)'}
                </div>
              </div>

              {selectedHistory.flex_message && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Flex Message JSON</div>
                  <pre className="rounded-md border p-2 max-h-40 overflow-auto text-xs font-mono">
                    {selectedHistory.flex_message}
                  </pre>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div>作成: {formatDate(selectedHistory.created)}</div>
                <div>更新: {formatDate(selectedHistory.updated)}</div>
                <div>送信開始: {formatDate(selectedHistory.sent_at)}</div>
                <div>完了: {formatDate(selectedHistory.completed_at)}</div>
              </div>

              {selectedHistory.tags && selectedHistory.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {selectedHistory.tags.map((tagId) => (
                    <Badge key={tagId} variant="outline" className="text-xs">
                      {tagId}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default NotificationHistory;
